import api from "@/plugins/api";
import { FUNDABLE_PROJECT_STATUSES } from "@/modules/projects/public";
/** Helper: unwrap BFF ApiResponse<T> envelope → T */
function unwrap(apiResponse) {
    return apiResponse.data;
}
/** Joins a multi-value filter into the comma-separated form the BFF/opdon search accepts, or undefined. */
function csv(values) {
    return values && values.length > 0 ? values.join(",") : undefined;
}
/**
 * Response header in which the BFF names the backend that served a donations search
 * (`Opdon` | `Opsearch`, OP-6445). OPSEARCH matches `q` against the indexed text fields, some of
 * which are not columns (note, project names, contact name); unlike the in-memory filter, it does not
 * match translated labels nor locale-formatted amounts and dates. OPDON matches the label only.
 */
export const DONATIONS_SEARCH_BACKEND_HEADER = "x-donations-search-backend";
export async function searchDonations(params) {
    return (await searchDonationsPage(params)).body;
}
export async function searchDonationsPage(params) {
    const response = await api.get("/portal/donations/search", {
        params: {
            q: params?.q,
            // Multi-value (IN) lists take precedence over the single-value variants.
            transactionStatus: csv(params?.statuses) ?? params?.status,
            paymentMethod: csv(params?.paymentMethods) ?? params?.paymentMethod,
            donationType: csv(params?.donationTypes) ?? params?.donationType,
            donationSource: csv(params?.sources),
            withoutDonationSource: params?.withoutSource,
            donorId: params?.donorId,
            contactId: params?.contactId,
            organizationId: params?.organizationId,
            campaignId: params?.campaignId,
            fromDonationDate: params?.dateFrom,
            toDonationDate: params?.dateTo,
            page: params?.page,
            pageSize: params?.pageSize,
            sortBy: params?.sortBy,
            sortOrder: params?.sortOrder,
            allowSearchIndex: params?.allowSearchIndex ? true : undefined,
        },
    });
    // Search returns ApiResponse<List<T>> — mapApiPaginated reads .data directly
    return {
        body: response.data,
        searchedByServer: response.headers?.[DONATIONS_SEARCH_BACKEND_HEADER] === "Opsearch",
    };
}
export async function getDonation(donationId) {
    const response = await api.get(`/portal/donations/${donationId}`);
    return unwrap(response.data);
}
export async function createDonation(payload) {
    const response = await api.post("/portal/donations", payload);
    return unwrap(response.data);
}
export async function updateDonation(donationId, payload) {
    const response = await api.put(`/portal/donations/${donationId}`, payload);
    return unwrap(response.data);
}
export async function deleteDonation(donationId) {
    await api.delete(`/portal/donations/${donationId}`);
}
export async function getDonationCatalogs() {
    const response = await api.get("/portal/donations/catalogs");
    return unwrap(response.data);
}
// ─── Donation comments (OP-5091) ───────────────────────────────────────────────
// BFF routes (api.ts prepends the /api/v1 base):
//   GET    /portal/donations/{donationId}/comments?page&per_page → paginated list
//   POST   /portal/donations/{donationId}/comments               → 201 created
//   PUT    /portal/comments/{commentId}                          → 200 updated
//   DELETE /portal/comments/{commentId}                          → 204 no content
// The BFF resolves the author display name from OPUS at read time,
// so the response shape already matches DonationCommentEntry one-to-one.
export async function listDonationComments(donationId, page = 1, perPage = 50) {
    const response = await api.get(`/portal/donations/${donationId}/comments`, {
        params: { page, per_page: perPage },
    });
    // These `/portal/...` calls don't set `unwrapEnvelope: true`, so the response
    // interceptor leaves the BFF envelope intact:
    //   { success, data: DonationCommentResponse[], pagination, message }
    // Read `data` (the array). Stay tolerant of an already-unwrapped bare array
    // or an interceptor-mapped `{ items }` shape, so this keeps working if the
    // call is later switched to the auto-unwrapping path.
    const body = response.data;
    if (Array.isArray(body))
        return body;
    if (Array.isArray(body?.data))
        return body.data;
    if (Array.isArray(body?.items))
        return body.items;
    return [];
}
// ─── Donation change history (OP-5075) ─────────────────────────────────────────
// BFF route (api.ts prepends the /api/v1 base):
//   GET /portal/donations/{donationId}/history?page&per_page&includeSystem
// Returns the BFF envelope { success, data: AuditLogEntryResponse[], pagination }.
// The frontend flattens each entry's `changes` into table rows (see donationService).
export async function listDonationHistory(donationId, page = 1, perPage = 100, includeSystem = false) {
    const response = await api.get(`/portal/donations/${donationId}/history`, {
        params: { page, per_page: perPage, includeSystem },
    });
    const body = response.data;
    if (Array.isArray(body))
        return body;
    if (Array.isArray(body?.data))
        return body.data;
    if (Array.isArray(body?.items))
        return body.items;
    return [];
}
/**
 * Searches projects (name/code) for the project picker, returning OPROJ's total
 * match count alongside the page.
 * BFF route: GET /portal/projects/search?q=&page=&pageSize= → ApiResponse<ProjectOption[]>
 * with the `pagination` envelope (page / perPage / totalItems / totalPages).
 */
/** Allocation pickers (create modal, Affectations tab) offer fundable projects only. */
export async function searchProjectsPaged(q, page = 1, pageSize = 20) {
    const response = await api.get(`/portal/projects/search`, {
        params: { q, page, pageSize, status: FUNDABLE_PROJECT_STATUSES },
        // CONSTRAINT: `status` is an array; axios' default spelling `status[]=A` is not bound by
        // ASP.NET Core, so the BFF would answer 200 and offer every status (R61).
        paramsSerializer: { indexes: null },
    });
    // `/portal/...` calls keep the BFF envelope intact: { success, data: ProjectOption[], pagination: {…} }
    const body = response.data;
    const items = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
            ? body
            : [];
    const totalItems = typeof body?.pagination?.totalItems === "number" ? body.pagination.totalItems : items.length;
    return { items, totalItems };
}
/** Searches projects (name/code), returning just the items. Thin wrapper over
 *  {@link searchProjectsPaged} for callers that don't need the total count. */
export async function searchProjects(q) {
    return (await searchProjectsPaged(q)).items;
}
export async function getDonationAllocations(donationId) {
    const response = await api.get(`/portal/donations/${donationId}/allocations`);
    return unwrap(response.data);
}
/**
 * Fetches the tenant's default project, used to pre-select the allocation when
 * opening the donation creation form (OP-5841 AC1).
 * BFF route: GET /portal/projects/default → ApiResponse<ProjectOption>.
 * Returns null when the tenant has no default configured (BFF 404) — the form
 * then leaves the (mandatory) allocation field empty for the user to fill.
 */
export async function getDefaultProject() {
    try {
        const response = await api.get(`/portal/projects/default`);
        const data = response.data?.data ?? response.data;
        return data?.projectId ? data : null;
    }
    catch (err) {
        if (err?.response?.status === 404) {
            return null;
        }
        throw err;
    }
}
export async function createDonationAllocation(donationId, payload) {
    const response = await api.post(`/portal/donations/${donationId}/allocations`, payload);
    return unwrap(response.data);
}
export async function updateDonationAllocation(donationId, allocationId, payload) {
    const response = await api.put(`/portal/donations/${donationId}/allocations/${allocationId}`, payload);
    return unwrap(response.data);
}
export async function deleteDonationAllocation(donationId, allocationId) {
    await api.delete(`/portal/donations/${donationId}/allocations/${allocationId}`);
}
export async function createDonationComment(donationId, content) {
    const response = await api.post(`/portal/donations/${donationId}/comments`, { content });
    return unwrap(response.data);
}
export async function updateDonationComment(commentId, content) {
    const response = await api.put(`/portal/comments/${commentId}`, { content });
    return unwrap(response.data);
}
export async function deleteDonationComment(commentId) {
    await api.delete(`/portal/comments/${commentId}`);
}
/**
 * Fetches one page of a donation's full tax-receipt history — the active receipt and every
 * cancelled one, newest first.
 */
export async function getTaxReceiptHistory(donationId, page = 1, perPage = 20) {
    const response = await api.get(`/portal/tax-receipts/by-donation/${donationId}/history`, {
        params: { page, perPage },
    });
    // `/portal/...` calls keep the BFF envelope intact: { success, data: TaxReceiptDto[], pagination }.
    const body = response.data;
    const items = Array.isArray(body)
        ? body
        : (body?.data ?? body?.items ?? []);
    return {
        items,
        totalItems: typeof body?.pagination?.totalItems === "number" ? body.pagination.totalItems : items.length,
        totalPages: typeof body?.pagination?.totalPages === "number" ? body.pagination.totalPages : 1,
    };
}
/** Fetches the per-donation tax receipt, or null when none has been generated yet. */
export async function getTaxReceiptByDonation(donationId) {
    const response = await api.get(`/portal/tax-receipts/by-donation/${donationId}`);
    return unwrap(response.data);
}
/** Fetches where the donation stands with respect to the annual tax receipt of its fiscal year. */
export async function getAnnualTaxReceiptStatus(donationId) {
    const response = await api.get(`/portal/tax-receipts/by-donation/${donationId}/annual-status`);
    return unwrap(response.data);
}
/** Generates (or returns the existing) per-donation tax receipt for a donation. */
export async function generateTaxReceipt(donationId) {
    const response = await api.post(`/portal/tax-receipts/by-donation/${donationId}/generate`);
    return unwrap(response.data);
}
/**
 * Cancels (annule) a tax receipt and returns the updated receipt. The motive is mandatory
 * (fixed catalogue, member name on the wire); the free-text comment is mandatory for `Other`.
 */
export async function cancelTaxReceipt(taxReceiptId, reasonCode, reason) {
    const response = await api.post(`/portal/tax-receipts/${taxReceiptId}/cancel`, {
        reasonCode,
        reason: reason ?? null,
    });
    return unwrap(response.data);
}
/**
 * Replaces a cancelled annual tax receipt with a new one for the same donor and fiscal year and
 * returns it (OP-7612). Refusals carry a `TaxReceipt.Regeneration.*` error code.
 */
export async function regenerateTaxReceipt(taxReceiptId) {
    const response = await api.post(`/portal/tax-receipts/${taxReceiptId}/regenerate`);
    return unwrap(response.data);
}
/** Revokes every public link already sent for a receipt and returns it with a freshly minted link. */
export async function revokeTaxReceiptPublicLink(taxReceiptId) {
    const response = await api.post(`/portal/tax-receipts/${taxReceiptId}/public-link/revoke`);
    return unwrap(response.data);
}
/** Re-sends a tax receipt by email and returns the updated receipt. */
export async function resendTaxReceiptEmail(taxReceiptId, recipientEmail) {
    const response = await api.post(`/portal/tax-receipts/${taxReceiptId}/send`, {
        recipientEmail: recipientEmail ?? null,
    });
    return unwrap(response.data);
}
/** Fetches the backend-valid next statuses for a donation's current transaction status. */
export async function getAvailableDonationTransitions(donationId) {
    const response = await api.get(`/portal/donations/${donationId}/transaction/status/available-transitions`);
    return unwrap(response.data);
}
/**
 * BFF route: POST /portal/donors/receipt-information:check → ApiResponse<DonorReceiptInformation>
 *
 * A POST for a read: the body carries either CRM references or a whole identity, neither of which
 * fits a query string. Nothing is written.
 */
export async function checkDonorReceiptInformation(query) {
    const response = await api.post("/portal/donors/receipt-information:check", query);
    return unwrap(response.data);
}
